import { useState } from 'react'
import type { KnowledgeBase } from '../../api/knowledgeBases.ts'
import { Empty } from './components/AsyncState.tsx'
import { presetName } from './presets.ts'
import { ago, bytes, num } from './lib/format.ts'

interface ListScreenProps {
  bases: KnowledgeBase[]
  includeArchived: boolean
  onIncludeArchived: (value: boolean) => void
  onOpen: (kbId: string) => void
  onCreate: () => void
}

/**
 * The base list.
 *
 * A table rather than a card grid, because of the wide display this is designed
 * for: a fixed-width card grid puts three bases in the top-left corner of a 2560px
 * screen and leaves two thirds of it empty. A table fills the width whatever the
 * count, and the space buys the counters that make a row worth scanning.
 */
export function ListScreen({
  bases,
  includeArchived,
  onIncludeArchived,
  onOpen,
  onCreate,
}: ListScreenProps) {
  const [filter, setFilter] = useState('')
  const needle = filter.trim().toLowerCase()
  const visible = needle
    ? bases.filter(
        (base) =>
          base.name.toLowerCase().includes(needle) ||
          (base.description ?? '').toLowerCase().includes(needle),
      )
    : bases

  return (
    <div className="pane">
      <div className="wb__title" style={{ marginBottom: 18 }}>
        <div className="wb__title-main">
          <h1 style={{ fontSize: 21 }}>Базы знаний</h1>
          <div className="wb__sub">
            Источники, которые вы загружаете, и wiki, которую по ним ведёт агент.
          </div>
        </div>
        <div className="filters">
          <input
            className="input"
            value={filter}
            placeholder="Поиск по названию"
            onChange={(event) => setFilter(event.target.value)}
          />
          <button
            type="button"
            className="btn btn--ghost"
            aria-pressed={includeArchived}
            onClick={() => onIncludeArchived(!includeArchived)}
          >
            {includeArchived ? 'Скрыть архив' : 'Показать архив'}
          </button>
          <button type="button" className="btn btn--primary" onClick={onCreate}>
            Создать базу
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        needle ? (
          <Empty title="Ничего не найдено">Ни одна база не подходит под «{filter}».</Empty>
        ) : (
          <Empty title="Пока ни одной базы">
            База знаний — это папка источников и wiki, которую по ним ведёт агент. Создайте первую,
            загрузите файл и запустите ingest.
          </Empty>
        )
      ) : (
        <div className="kb-table">
          <div className="table-scroll">
            <table className="kb">
              <thead>
                <tr>
                  <th>База</th>
                  <th>Пресет</th>
                  <th className="num">Источников</th>
                  <th className="num">Вес raw</th>
                  <th className="num">Страниц wiki</th>
                  <th className="num">Вес wiki</th>
                  <th>Последний ingest</th>
                  <th>Последний lint</th>
                  <th>HEAD</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((base) => (
                  <tr
                    key={base.id}
                    className={base.archived ? 'archived' : undefined}
                    onClick={() => onOpen(base.id)}
                    tabIndex={0}
                    role="link"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onOpen(base.id)
                      }
                    }}
                  >
                    <td>
                      <div className="kb-name">
                        {base.name}
                        {base.archived ? (
                          <span className="pill pill--warn pill--sm" style={{ marginLeft: 8 }}>
                            в архиве
                          </span>
                        ) : null}
                      </div>
                      {base.description ? (
                        <div className="kb-desc">{base.description}</div>
                      ) : (
                        <div className="kb-desc">Без описания.</div>
                      )}
                    </td>
                    <td>
                      <span className="pill pill--neutral pill--sm">
                        {presetName(base.preset_key || '—')}
                      </span>
                    </td>
                    <td className="num mono">{num(base.raw_files ?? 0)}</td>
                    <td className="num mono">{bytes(base.raw_bytes ?? 0)}</td>
                    <td className="num mono">{num(base.wiki_pages ?? 0)}</td>
                    <td className="num mono">{bytes(base.wiki_bytes ?? 0)}</td>
                    <td>{base.last_ingest_at ? ago(base.last_ingest_at) : '—'}</td>
                    <td>{base.last_lint_at ? ago(base.last_lint_at) : '—'}</td>
                    <td className="mono">
                      {base.git_head ? (
                        base.git_head.slice(0, 7)
                      ) : (
                        <span className="muted-2" title="История ещё не записана">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
